// Mufyn Routes Configuration
import { lazy } from "react";

// ============================================
// ADMIN VIEWS — lazy-loaded (code splitting)
// ------------------------------------------------------------------
// These views are only needed once the user is authenticated and
// navigates inside /admin. They pull in the heaviest dependencies of
// the app (apexcharts, jspdf, react-table, ...). Keeping them out of
// the initial bundle drastically speeds up the first paint, especially
// on mobile / slow connections. The auth views below stay eager so the
// first-open experience (welcome animation) is never delayed.
// ============================================
const Dashboard = lazy(() => import("views/Dashboard/Dashboard.js"));
const MyAccounting = lazy(() => import("views/Dashboard/MyAccounting.js"));
const Budgets = lazy(() => import("views/Dashboard/Budgets.js"));
const ProfilesManagement = lazy(() => import("views/Dashboard/ProfilesManagement.js"));
const MyAccount = lazy(() => import("views/Dashboard/MyAccount.js"));
const MySubscription = lazy(() => import("views/Dashboard/MySubscription.js"));
const SuperAdminMenu = lazy(() => import("views/Dashboard/SuperAdminMenu"));

// ============================================
// AUTH VIEWS — eager (kept in the initial bundle)
// Small, dependency-light screens shown on the very first open.
// ============================================
import SignIn from "views/Pages/SignIn.js";
import SignUp from "views/Pages/SignUp.js";
import ForgotPassword from "views/Pages/ForgotPassword.js";
import ResetPassword from "views/Pages/ResetPassword.js";
import LandingPage from "views/Pages/LandingPage.js";
import WelcomeScreen from "views/Pages/WelcomeScreen.js";
import FAQPage from "views/Pages/FAQPage.js";
import AccountDeletionPage from "views/Pages/AccountDeletionPage.js";
import Onboarding from "views/Pages/Onboarding.js";

import {
  HomeIcon,
  PersonIcon,
  DocumentIcon,
  RocketIcon,
  SupportIcon,
  CreditIcon,
} from "components/Icons/Icons";
import { FaWallet, FaLayerGroup, FaUser, FaCrown, FaChartPie } from 'react-icons/fa';
import { MdOutlineAdminPanelSettings } from 'react-icons/md';

var dashRoutes = [
  // ============================================
  // INVISIBLE ROUTES (not shown in sidebar)
  // ============================================
  {
    path: "/welcome",
    nameKey: "routes.welcome",
    component: WelcomeScreen,
    layout: "/auth",
    invisible: true,
  },
  {
    path: "/landing",
    nameKey: "routes.landing",
    component: LandingPage,
    layout: "/auth",
    invisible: true,
  },
  {
    path: "/faq",
    nameKey: "routes.faq",
    component: FAQPage,
    layout: "/auth",
    invisible: true,
  },
  {
    path: "/account-deletion",
    nameKey: "routes.account_deletion",
    component: AccountDeletionPage,
    layout: "/auth",
    invisible: true,
  },
  {
    path: "/onboarding",
    nameKey: "routes.onboarding",
    component: Onboarding,
    layout: "/auth",
    invisible: true,
  },
  {
    path: "/signin",
    nameKey: "routes.signin",
    icon: <DocumentIcon color='inherit' />,
    component: SignIn,
    layout: "/auth",
    invisible: true,
  },
  {
    path: "/signup",
    nameKey: "routes.signup",
    icon: <RocketIcon color='inherit' />,
    component: SignUp,
    layout: "/auth",
    invisible: true,
  },
  {
    path: "/forgot-password",
    nameKey: "routes.forgot_password",
    icon: <DocumentIcon color='inherit' />,
    component: ForgotPassword,
    layout: "/auth",
    invisible: true,
  },
  {
    path: "/reset-password",
    nameKey: "routes.reset_password",
    icon: <DocumentIcon color='inherit' />,
    component: ResetPassword,
    layout: "/auth",
    invisible: true,
  },

  // ============================================
  // VISIBLE ROUTES (shown in sidebar)
  // Order: Dashboard > Recurring > Accounting > Members > Profiles > Account > Subscription > Admin
  // ============================================
  
  // 1. Dashboard
  {
    path: "/dashboard",
    nameKey: "routes.dashboard",
    icon: <HomeIcon color='inherit' />,
    component: Dashboard,
    layout: "/admin",
    color: "purple",
  },
  
  // 2. My Accounting
  {
    path: "/accounting",
    nameKey: "routes.my_accounting",
    icon: <FaWallet color='inherit' />,
    component: MyAccounting,
    layout: "/admin",
    color: "blue",
  },
  
  // 3. Budgets
  {
    path: "/budgets",
    nameKey: "routes.budgets",
    icon: <FaChartPie color='inherit' />,
    component: Budgets,
    layout: "/admin",
    color: "cyan",
  },
  
  // 4. Profiles Management (licence owner only)
  {
    path: "/profiles",
    nameKey: "routes.profiles_management",
    icon: <FaLayerGroup color='inherit' />,
    component: ProfilesManagement,
    layout: "/admin",
    role: "license_owner",
  },
  
  // 5. My Account
  {
    path: "/account",
    nameKey: "routes.my_account",
    icon: <FaUser color='inherit' />,
    component: MyAccount,
    layout: "/admin",
  },
  
  // 6. My Subscription (licence owner only)
  {
    path: "/subscription",
    nameKey: "routes.my_subscription",
    icon: <FaCrown color='inherit' />,
    component: MySubscription,
    layout: "/admin",
    role: "license_owner",
  },
  
  // 7. Administration (super.admin only)
  {
    path: "/administration",
    nameKey: "routes.administration",
    icon: <MdOutlineAdminPanelSettings color='inherit' />,
    component: SuperAdminMenu,
    layout: "/admin",
    role: "super.admin",
    color: "red",
  },
];

export default dashRoutes;
